import { useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { trackError } from '@proposify/frontend-observability';
import { QueryKeys } from 'utils/queryKeys';
import { ContentType } from 'utils/getContentType';
import type { VariableNode } from 'services/entities/VariablesEntity';
import { useAppSelector } from 'components/editor/grid/reduxStore/Store';
import { selectSections } from 'components/editor/grid/reduxStore/editorSlice';
import { selectContentType, selectIsDocumentLocked } from 'components/editor/grid/reduxStore/documentSettingsSlice';
import { selectCrmVariablesIsError } from 'components/editor/grid/reduxStore/allVariableSlice';
import { collectVariableNodes, pruneOrphanedCrmRowsAcrossSections } from 'components/editor/grid/reduxStore/crmCountSync';
import { isGroupedArrayNode } from 'components/editor/GridDndEditor/Block/Table/tableUtils/deriveCrmRows';
import { useTableContentUndoRedo } from 'components/editor/hooks/useTableContentUndoRedo';
import { useCrmPricingEmbedFlag } from 'hooks/useCrmPricingEmbedFlag';
import { useCrmVariablesV2RouteFlag } from 'hooks/useCrmVariablesV2RouteFlag';
import { linkQueries } from './linkQueries';
import type { ContentLink, FlexDataLink } from './types';

export const useUnlinkCrmObject = (contentId: string) => {
  const queryClient = useQueryClient();
  const queryKey = QueryKeys.getContentFlexDataLinks(contentId);
  const crmVariablesQueryKey = [...QueryKeys.crmVariables, contentId];

  const sections = useAppSelector(selectSections);
  const sectionsRef = useRef(sections);
  useEffect(() => {
    sectionsRef.current = sections;
  }, [sections]);

  const contentType = useAppSelector(selectContentType);
  const isDocumentLocked = useAppSelector(selectIsDocumentLocked);
  const crmFetchFailed = useAppSelector(selectCrmVariablesIsError);
  const { handleTableContentChanged } = useTableContentUndoRedo();
  const crmPricingEmbedEnabled = useCrmPricingEmbedFlag();
  const { enabled: crmVariablesV2RouteEnabled } = useCrmVariablesV2RouteFlag();

  const {
    mutate: unlinkObject,
    isPending,
    isError,
  } = useMutation({
    ...linkQueries.unlink(contentId),
    onSuccess: async (_data, obj: FlexDataLink) => {
      queryClient.setQueryData<ContentLink[]>(queryKey, (prev) => (prev ?? []).filter((link) => link.toObject.objectId !== obj.objectId));
      queryClient.invalidateQueries({ queryKey });
      await queryClient.invalidateQueries({ queryKey: crmVariablesQueryKey });

      const useV2Route = crmVariablesV2RouteEnabled && crmPricingEmbedEnabled;
      const freshCrmData = queryClient.getQueryData<VariableNode[]>([...crmVariablesQueryKey, ...(useV2Route ? ['v2'] : [])]);
      const allArrayNodes = (freshCrmData ?? []).flatMap(collectVariableNodes).filter(isGroupedArrayNode);

      const shouldPruneCrmRows =
        !crmFetchFailed &&
        allArrayNodes.length === 0 &&
        !isDocumentLocked &&
        crmPricingEmbedEnabled &&
        crmVariablesV2RouteEnabled &&
        contentType !== ContentType.SECTION &&
        contentType !== ContentType.TEMPLATE;

      if (shouldPruneCrmRows) {
        const updates = pruneOrphanedCrmRowsAcrossSections(sectionsRef.current, allArrayNodes);

        updates.forEach((update) => {
          const previousContentTable = sectionsRef.current.find((section) => section.id === update.sectionId)?.blocksContent[update.blockId]
            ?.contentTable;
          if (!previousContentTable) return;
          handleTableContentChanged(update.blockId, update.sectionId, previousContentTable, update.contentTable);
        });
      }
    },
    onError: (error: Error) => {
      trackError(error);
      queryClient.invalidateQueries({ queryKey });
    },
  });

  return { unlinkObject, isPending, isError };
};
